import { createSlice } from '@reduxjs/toolkit';
import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
// utils
import API from '../../utils/apiConfig';
import { get, post, update } from '../../utils/server';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------
const actionsNames = {
  approve: 'approve',
  decline: 'decline',
  close: 'close',
  open: 'open',
  hide: 'hide',
  updating: 'updating',
  enableTM: 'enableTM',
  disableTM: 'disableTM',
};

const initialState = {
  isLoading: false,
  error: null,
  businesses: [],
  business: null,
  SIC_CODES_LIST: [],
  sortBy: null,
  actionsOn: [],
  alert: {
    show: false,
    title: 'Alert',
    messsage: '',
    type: 'error',
  },
};

const slice = createSlice({
  name: 'business',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET Businesses
    getBusinessesSuccess(state, action) {
      state.isLoading = false;
      state.businesses = action.payload;
    },

    getCustomerBusinessesSuccess(state, action) {
      state.isLoading = false;
      state.businesses = action.payload;
    },

    // GET Single Business
    getBusinessSuccess(state, action) {
      state.isLoading = false;
      state.business = action.payload;
    },
    getSICCodesListuccess(state, action) {
      state.isLoading = false;
      state.SIC_CODES_LIST = action.payload?.list;
    },

    // Actions //
    /// Actions for Customer
    approveBusinessSuccess(state, action) {
      state.alert = { show: true, title: 'Success', messsage: 'Business Successfully Approved', type: 'success' };
      state.business = { ...state.business, mlroStatus: 'APPROVED' };
    },

    declineBusinessSuccess(state, action) {
      state.alert = { show: true, title: 'Success', messsage: 'Business Successfully Declined', type: 'success' };
      state.business = { ...state.business, mlroStatus: 'DECLINED' };
    },

    hideBusinessSuccess(state, action) {
      state.alert = { show: true, title: 'Success', messsage: 'Business Successfully HIDDEN', type: 'success' };
      state.business = { ...state.business, status: 'HIDDEN' };
    },
    closeBusinessSuccess(state, action) {
      state.alert = { show: true, title: 'Success', messsage: 'Business Successfully Closed', type: 'success' };
      state.business = { ...state.business, status: 'CLOSED' };
    },
    openBusinessSuccess(state, action) {
      state.alert = { show: true, title: 'Success', messsage: 'Business Successfully Opened', type: 'success' };
      state.business = { ...state.business, status: 'OPEN' };
    },
    updateBusinessSuccess(state, action) {
      state.alert = { show: true, title: 'Success', messsage: 'Business Successfully Updated', type: 'success' };
      state.business = { ...state.business };
    },
    actionFailed(state, action) {
      state.alert = { show: true, title: 'Failed', messsage: action.payload.errors[0].message, type: 'error' };
    },

    // Actions started
    actionStarted(state, action) {
      state.actionsOn = [...state.actionsOn, action.payload];
    },
    actionEnd(state, action) {
      state.actionsOn = state.actionsOn.filter((actionType) => actionType !== action.payload);
    },

    enableTMBusinessSuccess(state, action) {
      state.alert = {
        show: true,
        title: 'Success',
        messsage: 'Transaction Monitoring Successfully Enabled',
        type: 'success',
      };

      // Only update the state with data from the API response
      if (action.payload) {
        state.business = {
          ...state.business,
          transaction_monitoring: action.payload,
        };
      } else {
        // Log error if no data received from API
        console.error('Received empty response data from enableTM API');
      }
    },

    disableTMBusinessSuccess(state, action) {
      state.alert = {
        show: true,
        title: 'Success',
        messsage: 'Transaction Monitoring Successfully Disabled',
        type: 'success',
      };

      // Only update the state with data from the API response
      if (action.payload) {
        state.business = {
          ...state.business,
          transaction_monitoring: action.payload,
        };
      } else {
        // Log error if no data received from API
        console.error('Received empty response data from disableTM API');
      }
    },
  },
});

// Reducer
export default slice.reducer;

export function getBusinesses(mlroStatus) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get(API.GET_BUSINESSES({ mlroStatus }));
      dispatch(slice.actions.getBusinessesSuccess(response.businesses));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCustomerBusinesses(customerId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get(API.GET_BUSINESSES({ customer_id: customerId }));
      dispatch(slice.actions.getCustomerBusinessesSuccess(response.businesses));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getBusiness(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get(API.GET_BUSINESS(id));
      dispatch(slice.actions.getBusinessSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBusinessNatures() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get(API.GET_SIC_CODES_LIST());
      dispatch(slice.actions.getSICCodesListuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ------------------- Actions -------------------- \\
export function approveBusiness(id, callBack, data) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.approve));
    try {
      const response = await post({ path: API.APPROVE_BUSINESS(id), data });
      callBack('Business Approved Successfuly', 'success');
      dispatch(slice.actions.approveBusinessSuccess(response.data));

      // Fetch the complete business data to update state with transaction monitoring
      try {
        const businessData = await get(API.GET_BUSINESS(id));
        dispatch(slice.actions.getBusinessSuccess(businessData));
      } catch (error) {
        console.error('Error fetching business after approval:', error);
      }

      dispatch(slice.actions.actionEnd(actionsNames.approve));
    } catch (error) {
      callBack(error?.errors[0]?.message, 'error');
      dispatch(slice.actions.actionFailed(error));
      dispatch(slice.actions.actionEnd(actionsNames.approve));
    }
  };
}

export function declineBusiness(id, callBack) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.decline));
    try {
      const response = await post({ path: API.DECLINE_BUSINESS(id) });
      callBack('Business Declined Successfuly', 'success');
      dispatch(slice.actions.declineBusinessSuccess(response.data));
      dispatch(slice.actions.actionEnd(actionsNames.decline));
    } catch (error) {
      callBack(error?.errors[0]?.message, 'error');
      dispatch(slice.actions.actionFailed(error));
      dispatch(slice.actions.actionEnd(actionsNames.decline));
    }
  };
}

export function openBusiness(id, callBack) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.open));
    try {
      const response = await post({ path: API.OPEN_BUSINESS(id) });
      callBack('Business Re-Opened Successfuly', 'success');
      dispatch(slice.actions.openBusinessSuccess(response.data));
      dispatch(slice.actions.actionEnd(actionsNames.open));
    } catch (error) {
      callBack(error?.errors[0]?.message, 'error');
      dispatch(slice.actions.hasError(error));
      dispatch(slice.actions.actionEnd(actionsNames.open));
    }
  };
}

export function closeBusiness(id, callBack) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.close));
    try {
      const response = await post({ path: API.CLOSE_BUSINESS(id) });
      callBack('Business Closed Successfuly', 'success');
      dispatch(slice.actions.closeBusinessSuccess(response.data));
      dispatch(slice.actions.actionEnd(actionsNames.close));
    } catch (error) {
      callBack(error?.errors[0]?.message, 'error');
      dispatch(slice.actions.hasError(error));
      dispatch(slice.actions.actionEnd(actionsNames.close));
    }
  };
}

export function hideBusiness(id, callBack) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.hide));

    try {
      const response = await post({ path: API.HIDE_BUSINESS(id) });
      callBack('Business Hidden Successfuly', 'success');
      dispatch(slice.actions.hideBusinessSuccess(response.data));
      dispatch(slice.actions.actionEnd(actionsNames.hide));
    } catch (error) {
      callBack(error?.errors[0]?.message, 'error');
      dispatch(slice.actions.hasError(error));
      dispatch(slice.actions.actionEnd(actionsNames.hide));
    }
  };
}

export function updateBusiness(data, callBack) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.updating));
    try {
      const response = await update({ path: API.UPDATE_BUSINESS(data.id), data });
      callBack('Business Updated Successfuly', 'success', true);
      dispatch(slice.actions.updateBusinessSuccess(response.data));

      // Fetch the complete business data to update state with transaction monitoring
      try {
        const businessData = await get(API.GET_BUSINESS(data.id));
        dispatch(slice.actions.getBusinessSuccess(businessData));
      } catch (error) {
        console.error('Error fetching business after update:', error);
      }

      dispatch(slice.actions.actionEnd(actionsNames.updating));
    } catch (error) {
      callBack(error?.errors[0]?.message, 'error');
      dispatch(slice.actions.hasError(error));
      dispatch(slice.actions.actionEnd(actionsNames.updating));
    }
  };
}

export function enableTMBusiness(id, callBack) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.actionStarted(actionsNames.enableTM));

    // Get current business state
    const { business } = getState().business;

    // Check if business is approved
    if (business && business.mlroStatus !== 'APPROVED') {
      callBack('Business must be approved before enabling transaction monitoring', 'error');
      dispatch(slice.actions.actionEnd(actionsNames.enableTM));
      return;
    }

    try {
      const response = await post({ path: API.ENABLE_TM_BUSINESS(id) });
      callBack('Transaction Monitoring Enabled Successfully', 'success');

      // Pass the response data to the reducer
      dispatch(slice.actions.enableTMBusinessSuccess(response.data));
    } catch (error) {
      callBack(error?.errors[0]?.message || 'Error enabling transaction monitoring', 'error');
      dispatch(slice.actions.actionFailed(error));
    } finally {
      dispatch(slice.actions.actionEnd(actionsNames.enableTM));
    }
  };
}

export function disableTMBusiness(id, callBack) {
  return async () => {
    dispatch(slice.actions.actionStarted(actionsNames.disableTM));
    try {
      const response = await post({ path: API.DISABLE_TM_BUSINESS(id) });
      callBack('Transaction Monitoring Disabled Successfully', 'success');
      dispatch(slice.actions.disableTMBusinessSuccess(response.data));
      dispatch(slice.actions.actionEnd(actionsNames.disableTM));
    } catch (error) {
      callBack(error?.errors[0]?.message || 'Error disabling transaction monitoring', 'error');
      dispatch(slice.actions.actionFailed(error));
      dispatch(slice.actions.actionEnd(actionsNames.disableTM));
    }
  };
}
